import React, { useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Camera, Upload, Loader2, Sparkles, MapPin, Star } from "lucide-react";
import { toast } from "sonner";

export default function ImageSearchModal({ trigger, city: cityProp = "", category: categoryProp = "" }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);
  const [city, setCity] = useState(cityProp);
  const [category, setCategory] = useState(categoryProp);
  const [cities, setCities] = React.useState([]);
  const [categories, setCategories] = React.useState([]);

  React.useEffect(() => {
    if (!open) return;
    setCity(cityProp);
    setCategory(categoryProp);
    api.get("/cities").then((r) => setCities(r.data)).catch(() => {});
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, [open, cityProp, categoryProp]);

  const pick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(f.type)) {
      toast.error("Only JPG, PNG or WEBP images supported");
      return;
    }
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setResults(null);
  };

  const run = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const params = new URLSearchParams();
      if (city) params.set("city", city);
      if (category) params.set("category", category);
      const qs = params.toString();
      const { data } = await api.post(`/search/by-image${qs ? "?" + qs : ""}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResults(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Image search failed");
    } finally {
      setBusy(false);
    }
  };

  const reset = () => { setFile(null); setPreview(""); setResults(null); };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" className="rounded-full gap-2" data-testid="open-image-search-btn">
            <Camera className="h-4 w-4" /> Search by image
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Search vendors by image</DialogTitle>
          <DialogDescription>
            Upload a photo of an item, product or storefront — we'll find matching businesses using AI vision.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">City</label>
            <select
              value={city}
              onChange={(e) => setCity(e.target.value)}
              data-testid="image-search-city-select"
              className="mt-1 w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="">All cities</option>
              {cities.map((c) => (<option key={c.name} value={c.name}>{c.name}</option>))}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Category</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              data-testid="image-search-category-select"
              className="mt-1 w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="">All categories</option>
              {categories.map((c) => (<option key={c.slug} value={c.name}>{c.name}</option>))}
            </select>
          </div>
        </div>

        {!preview && (
          <label
            data-testid="image-search-dropzone"
            className="block border-2 border-dashed border-border rounded-2xl p-10 text-center cursor-pointer hover:bg-muted/40 transition-colors"
          >
            <Upload className="h-8 w-8 mx-auto text-muted-foreground" />
            <div className="mt-3 font-medium">Choose an image</div>
            <div className="text-xs text-muted-foreground mt-1">JPG, PNG or WEBP, up to 6 MB</div>
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={pick}
              data-testid="image-search-file-input"
            />
          </label>
        )}

        {preview && (
          <div className="grid sm:grid-cols-[200px_1fr] gap-4 items-start">
            <img src={preview} alt="upload" className="w-full rounded-xl border border-border/60 object-cover aspect-square" />
            <div className="space-y-3">
              <div className="flex gap-2">
                <Button onClick={run} disabled={busy} className="rounded-full" data-testid="image-search-run-btn">
                  {busy ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Analysing…</> : <><Sparkles className="h-4 w-4 mr-1" /> Find vendors</>}
                </Button>
                <Button onClick={reset} variant="outline" className="rounded-full" data-testid="image-search-reset-btn">Choose another</Button>
              </div>
              {results && (
                <div>
                  <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground">AI keywords</div>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {(results.terms || []).map((t) => (
                      <Badge key={t} variant="secondary" data-testid={`image-search-term-${t}`}>{t}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {results && (
          <div className="mt-3 max-h-[55vh] overflow-y-auto" data-testid="image-search-results">
            <div className="text-sm text-muted-foreground mb-2">{results.items.length} matching vendors</div>
            <div className="space-y-2">
              {results.items.map((v) => (
                <Link
                  key={v.vendor_id}
                  to={`/vendors/${v.vendor_id}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`image-search-result-${v.vendor_id}`}
                  className="flex items-center gap-3 p-3 rounded-xl border border-border/60 hover:bg-muted/40 transition-colors"
                >
                  <img src={v.logo} alt="" onError={(e)=>{e.currentTarget.style.background='linear-gradient(135deg,#f97316,#eab308)';e.currentTarget.removeAttribute('src');}} className="h-12 w-12 rounded-xl object-cover bg-muted" />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">{v.business_name}</div>
                    <div className="text-xs text-muted-foreground truncate">
                      {v.category} · <MapPin className="h-3 w-3 inline" /> {v.city}
                    </div>
                  </div>
                  <div className="text-sm flex items-center gap-1">
                    <Star className="h-3.5 w-3.5 fill-yellow-400 text-yellow-400" /> {v.rating?.toFixed(1)}
                  </div>
                </Link>
              ))}
              {results.items.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-6">No matches found. Try a clearer image.</p>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
